"use client";

import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  AlertTriangle,
  UserPlus,
  Settings,
  Menu,
  Phone,
  Tag,
  Star,
  Upload,
  Image,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

interface NavItemProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
}

function NavItem({ href, label, icon, isActive }: NavItemProps) {
  return (
    <Link
      to={href}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-all hover:bg-sidebar-accent",
        isActive
          ? "bg-sidebar-accent text-sidebar-foreground"
          : "text-sidebar-foreground/70"
      )}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

export function Sidebar() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isMobile]);

  const navItems = [
    {
      href: "/dashboard",
      label: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      href: "/citizens",
      label: "Citizens",
      icon: <Users className="h-5 w-5" />,
    },
    {
      href: "/reports",
      label: "Reports",
      icon: <AlertTriangle className="h-5 w-5" />,
    },
    {
      href: "/requests",
      label: "Citizen Requests",
      icon: <UserPlus className="h-5 w-5" />,
    },
    {
      href: "/contacts",
      label: "Contact Us",
      icon: <Phone className="h-5 w-5" />,
    },
    {
      href: "/tags",
      label: "Tags",
      icon: <Tag className="h-5 w-5" />,
    },
    {
      href: "/featured",
      label: "Featured",
      icon: <Star className="h-5 w-5" />,
    },
    {
      href: "/upload",
      label: "Upload",
      icon: <Upload className="h-5 w-5" />,
    },
    {
      href: "/external-images",
      label: "External Images",
      icon: <Image className="h-5 w-5" />,
    },
    {
      href: "/settings",
      label: "Settings",
      icon: <Settings className="h-5 w-5" />,
    },
  ];

  return (
    <>
      {isMobile && (
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-3 left-3 z-50"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      )}

      <aside
        className={cn(
          "bg-sidebar flex h-screen flex-col border-r transition-all duration-300 ease-in-out",
          isMobile
            ? isOpen
              ? "fixed inset-y-0 left-0 z-40 w-64 transform translate-x-0"
              : "fixed inset-y-0 left-0 z-40 w-64 transform -translate-x-full"
            : "w-64"
        )}
      >
        <div className="flex h-16 items-center border-b border-sidebar-border px-6">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="font-bold text-xl text-sidebar-foreground">
              Admin Panel
            </span>
          </Link>
        </div>

        <div className="flex flex-1 flex-col gap-1 overflow-auto px-3 py-4">
          {navItems.map((item) => (
            <NavItem
              key={item.href}
              href={item.href}
              label={item.label}
              icon={item.icon}
              isActive={
                location.pathname === item.href ||
                location.pathname?.startsWith(`${item.href}/`)
              }
            />
          ))}
        </div>
      </aside>

      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
